import { ModuleWrapper } from '@/components'
import { PageType } from '@/modules/common/consts'
import { Module } from '@/modules/companies'
import { useModuleConfigs } from '@/modules/companies/CompanySettings/hooks'
import {
  asNullableString,
  asString,
  asStringArrayOrEmpty,
  parseDateRangeOrDefault,
} from '@/utils'
import {
  useAdmin,
  useAutoSelectSingleLeaderGroup,
  useLeaderGroups,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, startOfMonth } from 'date-fns'
import { useMemo } from 'react'
import { InfringementStatuses } from '../consts'
import { InfringementsFilters } from '../components/filters/infringements-filters'
import { InfringementStatistics } from '../components/infringement-statistics'
import { InfringementsTable } from '../components/table/InfringementsTable'
import type { CombinedInfringementSource } from '../components/table/combined-rows'
import { ALL_INFRINGEMENT_SOURCES } from '../components/table/combined-rows'
import type {
  InfringementStatus,
  InfringementsTableFilter,
  SeverityCategory,
} from '../types'

const DEFAULT_DATE_RANGE = {
  from: startOfMonth(new Date()),
  to: endOfMonth(new Date()),
}

interface InfringementsProps {
  title?: string
  module?: Module
  storageName?: string
  detailPathBase?: string
}

export const Infringements: React.FC<InfringementsProps> = ({
  title,
  module = Module.INFRINGEMENTS,
  storageName = 'infringement-filters',
  detailPathBase = '/infringements',
}) => {
  const translations = useTranslate({
    infringements: 'common.infringements',
  })

  const admin = useAdmin()
  const me = useUser()
  const { hasPermissions } = usePermissions()

  const { myLeaderGroups, groupsLoading } = useLeaderGroups(module)
  const hasLeaderGroups = myLeaderGroups.length > 0

  const canViewOthers = hasPermissions(
    PERMISSIONS.infringements.view.othersinfringement,
    true
  )

  // Working time and tachograph use infringements show while the company has
  // their module on.
  const { active: activeModules } = useModuleConfigs()
  const availableSources = useMemo<CombinedInfringementSource[]>(
    () => [
      'DRIVING_REST',
      ...(activeModules.workInfringements ? (['WORKING_TIME'] as const) : []),
      ...(activeModules.tachographInfringements
        ? (['TACHOGRAPH_USE'] as const)
        : []),
    ],
    [activeModules.tachographInfringements, activeModules.workInfringements]
  )

  const {
    formValues: filter,
    formChangeHandler: filterHandler,
    updateForm,
    loadStoredFormState,
  } = useForm<InfringementsTableFilter>({
    values: {
      query: '',
      orderBy: '-infringementTimestamp',
      userOrderBy: '-firstName',
      excludeSeverities: [],
      excludeCategories: [],
      statuses: ['OPEN'],
      users: canViewOthers ? [] : [me.id],
      dateRange: DEFAULT_DATE_RANGE,
      leaderGroup: null,
      // No type picked lists every available type.
      sources: [],
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: storageName,
        sessionStorage: true,
        transformValues: {
          orderBy: asString,
          userOrderBy: asString,
          // No status picked lists every status.
          statuses: value => {
            const allowed = new Set<string>(Object.keys(InfringementStatuses))
            return asStringArrayOrEmpty(value).filter(
              (item): item is InfringementStatus => allowed.has(item)
            )
          },
          users: asStringArrayOrEmpty,
          dateRange: parseDateRangeOrDefault,
          leaderGroup: asNullableString,
          sources: value => {
            const allowed = new Set<string>(ALL_INFRINGEMENT_SOURCES)
            const sources = asStringArrayOrEmpty(value).filter(
              (item): item is CombinedInfringementSource => allowed.has(item)
            )
            return sources
          },
          excludeSeverities: value => {
            const severities = new Set<SeverityCategory>([
              'MINOR',
              'SERIOUS',
              'VERY_SERIOUS',
              'MOST_SERIOUS',
            ])
            return asStringArrayOrEmpty(value).filter(
              (item): item is SeverityCategory =>
                severities.has(item as SeverityCategory)
            )
          },
          excludeCategories: asStringArrayOrEmpty,
        },
      },
    },
  })

  useAutoSelectSingleLeaderGroup({
    canViewOthers: canViewOthers && hasLeaderGroups && !groupsLoading,
    isAdmin: admin,
    leaderGroup: filter.leaderGroup ?? undefined,
    leaderGroups: myLeaderGroups,
    loadStoredFormState,
    updateForm,
    onAutoSelect: group => {
      updateForm({ leaderGroup: group.id })
    },
  })

  // The picked types that are still available; a type can drop out when its
  // module is switched off. Picking all of them is the same as picking none.
  const pickedSources = useMemo(() => {
    const picked = (filter.sources ?? []).filter(source =>
      availableSources.includes(source)
    )
    return picked.length === availableSources.length ? [] : picked
  }, [availableSources, filter.sources])
  const shownFilter = useMemo<InfringementsTableFilter>(
    () => ({
      ...filter,
      sources: pickedSources.length > 0 ? pickedSources : availableSources,
    }),
    [availableSources, filter, pickedSources]
  )

  return (
    <ModuleWrapper
      title={title ?? translations.infringements}
      page={PageType.INFRINGEMENTS}
    >
      <InfringementsFilters
        filter={shownFilter}
        updateForm={updateForm}
        module={module}
        leaderGroups={myLeaderGroups}
        canViewOthers={canViewOthers}
        availableSources={availableSources}
        pickedSources={pickedSources}
      />

      {canViewOthers && <InfringementStatistics filter={shownFilter} />}
      <InfringementsTable
        filter={shownFilter}
        detailPathBase={detailPathBase}
        onSort={filterHandler('orderBy')}
      />
    </ModuleWrapper>
  )
}
